import { CalculsFormCalendrierAssistance } from '@norma/shared/constants/calculs';
import { fCurrency } from '@norma/shared/helpers/formatNumber';
import { getShouldNotDisplayCapitalisation } from '@norma/shared/helpers/prejudices/capitalisation';
import { Bareme } from '@norma/shared/types/bareme.type';
import { MonetaryErosion } from '@norma/shared/types/monetaryErosion.type';
import { PrejudiceFormCalendrierAssistanceCapitalisation } from '@norma/shared/types/prejudice.type';
import { Procedure } from '@norma/shared/types/procedure.type';
import { Victime } from '@norma/shared/types/victime.type';
import { Paragraph, Table, TableOfContents } from 'docx';
import i18next from 'i18next';
import { sortBy } from 'lodash';
import { getEmptyLine, getParagraph } from '../../docxFunctions';
import { debitSoldeAEchoirPrint } from '../debitSoldeAEchoirPrint';
import { prejudiceFormTable } from '../prejudiceFormTablePrint';
import { prejudiceNotesPrint } from '../prejudiceNotesPrint';
import { rentesPrint } from '../rentesPrint';
import { getMontantRevalorisePrint } from '../revalorisationPrint';
import { totalCapitalisationPrint } from '../totalCapitalisationPrint';
import {
  calendrierAssistanceRecapitulatif,
  calendrierAssistanceTableRows,
} from './calendrierAssistancePrint';

export const calendrierAssistanceCapitalisationPrint = ({
  victime,
  formData,
  tiersPayeurs,
  procedure,
  monetaryErosions,
  baremes,
}: {
  victime: Victime;
  formData: PrejudiceFormCalendrierAssistanceCapitalisation;
  tiersPayeurs: string[];
  procedure: Procedure;
  monetaryErosions: MonetaryErosion[];
  baremes: Bareme[];
}): (Paragraph | Table | TableOfContents)[] => {
  const rowsPrint = calendrierAssistanceTableRows({
    dateLiquidation: procedure.dateLiquidation,
    monetaryErosions,
    tiersPayeurs,
    revalorisationCoefficientsType: formData.revalorisationCoefficientsType,
  }).filter((row) => row.name !== 'joursHospitalisationADeduire');
  const lastRow = rowsPrint[rowsPrint.length - 1];
  const table = prejudiceFormTable(
    sortBy(formData.rows, (row) => row.dateDebut),
    [
      ...rowsPrint
        .slice(0, rowsPrint.length - 1)
        .filter(
          (row) =>
            ![
              'dateJustificatif',
              'montantsDejaRevalorises',
              'montantTotal',
              'priseEnChargeTiersPayeurs',
              'deductionFiscale',
              'resteACharge',
            ].includes(row.name),
        ),
      {
        name: 'annualiseMontantTotal',
        headerLabel: i18next.t(
          'prejudice.prejudicesFormTypes.CALENDRIER_ASSISTANCE.fields.rows.annualiseMontantTotal.columnHeader',
        ),
        render: (row) =>
          row.annualiseMontantTotal || row.annualiseMontantTotal === 0
            ? fCurrency(row.annualiseMontantTotal)
            : '',
      },
      {
        name: 'annualiseMontantTotal',
        headerLabel: i18next.t(
          'prejudice.prejudicesFormTypes.CALENDRIER_ASSISTANCE.fields.rows.annualiseMontantTotal.print.revalorise.columnHeader',
        ),
        render: (row) =>
          row.montantsDejaRevalorises
            ? ''
            : getMontantRevalorisePrint({
                montant: row.resteACharge,
                montantsDejaRevalorises: row.montantsDejaRevalorises,
                anneeOrDateMontant: row.dateJustificatif || undefined,
                monetaryErosions,
                anneeOrDateLiquidation: procedure.dateLiquidation || undefined,
                displayStar: false,
                coefficientsType: formData.revalorisationCoefficientsType,
              }),
      },
      ...rowsPrint.filter((row) =>
        [
          'dateJustificatif',
          'montantsDejaRevalorises',
          'montantTotal',
          'priseEnChargeTiersPayeurs',
          'deductionFiscale',
          'autresDeductions',
          'resteACharge',
        ].includes(row.name),
      ),
      ...(lastRow ? [lastRow] : []),
    ],
  );
  const indemniteGlobaleARepartir =
    CalculsFormCalendrierAssistance.getAssistanceIndemnitesRepartie({
      ...formData,
      partResponsabilite: procedure.partResponsabilite,
      tiersPayeurs: procedure.tiersPayeurs,
      dateLiquidation: procedure.dateLiquidation
        ? new Date(procedure.dateLiquidation)
        : undefined,
      monetaryErosions,
      dateConsolidation: procedure.dateConsolidation
        ? new Date(procedure.dateConsolidation)
        : undefined,
      dateDeces: victime.dateDeces ? new Date(victime.dateDeces) : undefined,
    });
  const debitSoldeTable = debitSoldeAEchoirPrint({
    indemniteGlobaleARepartir,
    partResponsabilite: procedure.partResponsabilite,
  });
  const victimeTotalCapitalise =
    (formData.victimeCoefficientCapitalisation || 0) *
    formData.victimeSommeACapitaliser;
  const totaux = CalculsFormCalendrierAssistance.totaux({
    rows: formData.rows,
    partResponsabilite: procedure.partResponsabilite,
    tiersPayeurs,
    dateLiquidation: procedure.dateLiquidation
      ? new Date(procedure.dateLiquidation)
      : undefined,
    monetaryErosions,
    revalorisationCoefficientsType: formData.revalorisationCoefficientsType,
  });
  const bareme = baremes.find(
    (bareme) => bareme._id === formData.baremeCapitalisation,
  );
  const totalCapitalisation = totalCapitalisationPrint({
    title: i18next.t('prejudice.capitalisation.print.title'),
    victime,
    procedure,
    titleVictime: i18next.t(
      'prejudice.capitalisation.form.victime.print.title',
    ),
    titleTiersPayeurs: i18next.t(
      'prejudice.capitalisation.form.tiersPayeurs.print.title',
    ),
    victimeSommeACapitaliser: {
      value: formData.victimeSommeACapitaliser,
    },
    victimeCoefficientCapitalisation: {
      value: formData.victimeCoefficientCapitalisation,
    },
    bareme: bareme && {
      value: bareme,
    },
    tiersPayeursTotalCapitalise: {
      value: formData.tiersPayeursTotalCapitalise,
    },
    victimeTotalCapitalise: {
      value: victimeTotalCapitalise,
    },
    ageDernierArrerage: {
      value: formData.ageDernierArrerage,
    },
    isLastArrerageViager: {
      value: Boolean(formData.isLastArrerageViager),
    },
    tiersPayeurs: formData.capitalisationTiersPayeurs.parTiersPayeur,
    allTiersPayeurs: procedure.tiersPayeurs,
  });
  const rentes =
    formData.rentes?.montant || formData.rentes?.montant === 0
      ? rentesPrint({ montant: formData.rentes.montant })
      : [];
  const notesPrint = prejudiceNotesPrint(formData.notes);
  const shouldNotDisplayCapitalisation = getShouldNotDisplayCapitalisation({
    dateConsolidation: procedure.dateConsolidation
      ? new Date(procedure.dateConsolidation)
      : undefined,
    dateLiquidation: procedure.dateLiquidation
      ? new Date(procedure.dateLiquidation)
      : undefined,
    dateDeces: victime.dateDeces ? new Date(victime.dateDeces) : undefined,
  });
  return [
    ...(notesPrint ? [notesPrint] : []),
    getEmptyLine(),
    ...(table ? table : []),
    getParagraph(i18next.t('prejudice.revalorisation.print.footnote') || ''),
    getEmptyLine(),
    ...calendrierAssistanceRecapitulatif({
      totaux,
      tiersPayeurs,
      isCapitalisation: true,
    }),
    getEmptyLine(),
    ...(!formData.isRentesOption && !shouldNotDisplayCapitalisation
      ? [...totalCapitalisation, getEmptyLine()]
      : []),
    ...debitSoldeTable,
    ...(formData.isRentesOption === true && !shouldNotDisplayCapitalisation
      ? [getEmptyLine(), ...rentes]
      : []),
  ];
};
